/*jshint esversion: 11 */

import {t} from "../i18n.js";
import {
    buildInstallmentLabel,
    calculateTotalAmountWithSurcharge,
    parseInstallmentPricing,
} from "./installment-pricing";

const SDK_URL = "https://sdk.mercadopago.com/js/v2";
const SECURITY_URL = "https://www.mercadopago.com/v2/security.js";

function parseAmount(rawValue) {
    const parsed = Number.parseFloat(String(rawValue ?? "0").replace(",", "."));
    return Number.isFinite(parsed) ? parsed : 0;
}

function ensureExternalScript(src, attributes = {}) {
    const existing = document.querySelector(`script[src="${src}"]`);

    if (existing) {
        return existing.dataset.loaded === "true"
            ? Promise.resolve()
            : new Promise((resolve, reject) => {
                existing.addEventListener("load", resolve, {once: true});
                existing.addEventListener("error", reject, {once: true});
            });
    }

    return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = src;
        script.async = true;

        Object.entries(attributes).forEach(([key, value]) => {
            script.setAttribute(key, value);
        });

        script.addEventListener("load", () => {
            script.dataset.loaded = "true";
            resolve();
        }, {once: true});
        script.addEventListener("error", reject, {once: true});

        document.head.appendChild(script);
    });
}

function clearSelect(element, placeholder) {
    if (!element) {
        return;
    }

    element.innerHTML = "";

    const option = document.createElement("option");
    option.textContent = placeholder;
    option.disabled = true;
    option.selected = true;

    element.appendChild(option);
}

function createSelectOptions(element, options, map = {label: "name", value: "id"}) {
    if (!element) {
        return;
    }

    element.innerHTML = "";

    const fragment = document.createDocumentFragment();

    options.forEach((optionData) => {
        const option = document.createElement("option");
        option.value = optionData[map.value];
        option.textContent = optionData[map.label];
        fragment.appendChild(option);
    });

    const wrapper = element.parentElement?.parentElement;

    if (wrapper) {
        wrapper.classList.toggle("hidden", options.length <= 1);
    }

    element.appendChild(fragment);
}

function notifyError(message) {
    if (window.Util?.notify) {
        window.Util.notify(message, "error");
        return;
    }

    console.error(message);
}

async function initMercadoPagoCardForm(root) {
    if (root.dataset.initialized === "true") {
        return;
    }

    root.dataset.initialized = "true";

    await ensureExternalScript(SECURITY_URL, {view: "checkout", output: "deviceId"});
    await ensureExternalScript(SDK_URL);

    const {
        publicKey,
        customerCards,
        amountInputId,
        formId,
        paymentMethodInputId,
        submitButtonId,
        identificationTypeInputId,
        identificationNumberInputId,
        cardholderNameInputId,
        tokenInputId,
        cardBinInputId,
        cardIdInputId,
        issuerSelectId,
        installmentsSelectId,
        requiredMethod,
    } = root.dataset;

    const parsedCustomerCards = customerCards ? JSON.parse(customerCards) : [];
    const amountElement = document.getElementById(amountInputId);
    const formElement = document.getElementById(formId);
    const paymentMethodElement = document.getElementById(paymentMethodInputId);
    const submitButton = document.getElementById(submitButtonId);
    const identificationTypeElement = document.getElementById(identificationTypeInputId);
    const identificationNumberElement = document.getElementById(identificationNumberInputId);
    const cardholderNameElement = document.getElementById(cardholderNameInputId);
    const tokenElement = document.getElementById(tokenInputId);
    const cardBinElement = document.getElementById(cardBinInputId);
    const cardIdElement = document.getElementById(cardIdInputId);
    const issuerElement = document.getElementById(issuerSelectId);
    const installmentsElement = document.getElementById(installmentsSelectId);

    const issuerPlaceholder = issuerElement?.dataset.placeholder ?? t("Banco emissor");
    const installmentsPlaceholder = installmentsElement?.dataset.placeholder ?? t("Parcelas");
    const maxInstallments = Math.max(1, Number.parseInt(root.dataset.maxInstallments ?? "1", 10) || 1);
    const pricingRules = parseInstallmentPricing(root.dataset.installmentPricing);

    const mp = new window.MercadoPago(publicKey);

    const cardNumberElement = mp.fields.create("cardNumber", {placeholder: t("Número do cartão")})
        .mount("form-checkout__cardNumber");
    const expirationDateElement = mp.fields.create("expirationDate", {placeholder: "MM/YY"})
        .mount("form-checkout__expirationDate");
    const securityCodeElement = mp.fields.create("securityCode", {placeholder: t("Código de segurança")})
        .mount("form-checkout__securityCode");

    if (issuerElement?.parentElement?.parentElement) {
        issuerElement.parentElement.parentElement.classList.add("hidden");
    }

    let currentBin = null;

    const updatePCIFields = (paymentMethod) => {
        const settings = paymentMethod.settings[0];
        cardNumberElement.update({settings: settings.card_number});
        securityCodeElement.update({settings: settings.security_code});
    };

    const fetchPaymentMethod = async (bin) => {
        const {results} = await mp.getPaymentMethods({bin});
        return results[0];
    };

    const fetchIssuers = async (paymentMethodId, bin) => mp.getIssuers({paymentMethodId, bin});

    const updateIssuer = async (paymentMethod, bin) => {
        let options = [paymentMethod.issuer];

        if (paymentMethod.additional_info_needed.includes("issuer_id")) {
            options = await fetchIssuers(paymentMethod.id, bin);
        }

        createSelectOptions(issuerElement, options);
    };

    const updateInstallments = async () => {
        const baseAmount = parseAmount(amountElement?.value);
        const processingFee = parseAmount(amountElement?.dataset.processingFee);
        const options = [];

        for (let installment = 1; installment <= maxInstallments; installment += 1) {
            options.push({
                installments: installment,
                recommended_message: buildInstallmentLabel(
                    calculateTotalAmountWithSurcharge(baseAmount, pricingRules, installment, processingFee),
                    installment
                ),
            });
        }

        createSelectOptions(installmentsElement, options, {
            label: "recommended_message",
            value: "installments",
        });
    };

    const updateAllByBin = async (bin) => {
        if (!bin) {
            clearSelect(issuerElement, issuerPlaceholder);
            clearSelect(installmentsElement, installmentsPlaceholder);

            if (paymentMethodElement) {
                paymentMethodElement.value = "";
            }

            return;
        }

        const paymentMethod = await fetchPaymentMethod(bin);
        currentBin = bin;

        if (paymentMethodElement) {
            paymentMethodElement.value = paymentMethod.id;
        }

        updatePCIFields(paymentMethod);
        await updateIssuer(paymentMethod, bin);
        await updateInstallments();
    };

    cardNumberElement.on("binChange", async (event) => {
        const bin = event.bin;

        tokenElement.value = "";
        cardBinElement.value = "";

        if (bin && bin !== currentBin) {
            await updateAllByBin(bin);
        }

        if (!bin) {
            clearSelect(issuerElement, issuerPlaceholder);
            clearSelect(installmentsElement, installmentsPlaceholder);
        }
    });

    if (cardIdElement) {
        cardIdElement.addEventListener("change", async () => {
            tokenElement.value = "";
            cardBinElement.value = "";

            if (!cardIdElement.value) {
                return;
            }

            const selectedCard = parsedCustomerCards.find((card) => String(card.id) === String(cardIdElement.value));

            if (!selectedCard) {
                return;
            }

            await updateAllByBin(selectedCard.first_six_digits);
            currentBin = selectedCard.first_six_digits;
        });
    }

    window.addEventListener("updateTotalAmount", async (event) => {
        if (amountElement) {
            amountElement.value = event.detail.amount;

            if ("processingFee" in (event.detail ?? {})) {
                amountElement.dataset.processingFee = event.detail.processingFee;
            }
        }

        await updateInstallments();
    });

    window.addEventListener("update-mp-amount", async (event) => {
        if (amountElement && event.detail.amount) {
            amountElement.value = event.detail.amount;
        }

        await updateInstallments();
    });

    await updateInstallments();

    const createCardToken = async (event) => {
        if (requiredMethod && document.getElementById("payment_method")?.value !== requiredMethod) {
            return;
        }

        event.preventDefault();

        if (identificationNumberElement) {
            identificationNumberElement.value = identificationNumberElement.value.replace(/\D/g, "");
        }

        if (submitButton) {
            submitButton.disabled = true;
        }

        let cardIdPayload = {};

        if (cardIdElement?.value) {
            cardNumberElement.unmount();
            expirationDateElement.unmount();
            cardIdPayload = {cardId: cardIdElement.value};
        }

        if (!tokenElement.value) {
            try {
                const token = await mp.fields.createCardToken({
                    cardholderName: cardholderNameElement?.value ?? "",
                    identificationType: identificationTypeElement?.value ?? "",
                    identificationNumber: identificationNumberElement?.value ?? "",
                    ...cardIdPayload,
                });

                tokenElement.value = btoa(token.id);
                cardBinElement.value = btoa(token.first_six_digits);
            } catch (error) {
                console.error("Erro ao criar token:", error);

                if (submitButton) {
                    submitButton.disabled = false;
                }

                return;
            }
        }

        if (
            installmentsElement
            && installmentsElement.value === ""
            && installmentsElement.classList.contains("hidden") === false
        ) {
            notifyError(t("Selecione o número de parcelas"));

            if (submitButton) {
                submitButton.disabled = false;
            }

            return;
        }

        if (tokenElement.value && cardBinElement.value.length >= 6) {
            formElement?.submit();
            return;
        }

        notifyError(t("Verifique os dados inseridos"));

        if (submitButton) {
            submitButton.disabled = false;
        }
    };

    formElement?.addEventListener("submit", createCardToken);
}

function bootstrapMercadoPagoCardForms() {
    document.querySelectorAll("[data-mp-card-form]").forEach((root) => {
        initMercadoPagoCardForm(root).catch((error) => {
            root.dataset.initialized = "false";
            console.error("Falha ao inicializar formulario Mercado Pago:", error);
        });
    });
}

document.addEventListener("DOMContentLoaded", bootstrapMercadoPagoCardForms);
